import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLES, ROLE_DESCRIPTIONS, ROLE_LABELS, type Role } from '@seller/shared';
import { api, ApiError, type InviteResponse, type ManagedUser } from '../api/client.js';
import { useSession } from '../auth/useSession.js';
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
  TableScroll,
} from '../components/ui.js';
import { dateTime } from '../format.js';

/**
 * User administration.
 *
 * Creating an account sends an invitation: a link to set a password and enroll an
 * authenticator. When mail is configured the link is emailed; when it is not, the
 * link — and a one-time password, so the old hand-off still works — is shown here
 * exactly once for the administrator to pass on in person. Resend re-sends an
 * invitation (retiring the old link); Send password reset does the same for an
 * account that is already set up. Nothing reaches the data until the password and
 * authenticator are both done.
 */

/** A one-time notice, optionally carrying a link and/or a one-time password. */
interface Notice {
  text: string;
  secret?: string;
  link?: string;
}

export function AdminUsersPage() {
  const session = useSession();
  const selfId = session.status === 'signedIn' ? session.user.id : null;
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const users = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ users: ManagedUser[] }>('/api/users'),
  });

  // Deactivated accounts are fetched with the rest — there are a handful of
  // accounts here, not thousands — and simply not listed until asked for.
  const all = users.data?.users ?? [];
  const inactive = all.filter((user) => !user.isActive);
  const listed = showInactive ? inactive : all.filter((user) => user.isActive);

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['users'] });
  }

  function fail(caught: unknown, fallback: string) {
    setNotice(null);
    setError(caught instanceof ApiError ? caught.message : fallback);
  }

  const create = useMutation({
    mutationFn: (input: { email: string; displayName: string; role: Role }) =>
      api.post<InviteResponse>('/api/users', input),
    onSuccess: async (result) => {
      setError(null);
      setNotice(inviteNotice(result, `Account created for ${result.user.email}.`));
      await refresh();
    },
    onError: (caught: unknown) => fail(caught, 'Could not create the account.'),
  });

  const resendInvite = useMutation({
    mutationFn: (id: string) => api.post<InviteResponse>(`/api/users/${id}/invite`),
    onSuccess: async (result) => {
      setError(null);
      setNotice(inviteNotice(result, `Invitation re-sent for ${result.user.email}.`));
      await refresh();
    },
    onError: (caught: unknown) => fail(caught, 'Could not resend the invitation.'),
  });

  const sendReset = useMutation({
    mutationFn: (id: string) => api.post<InviteResponse>(`/api/users/${id}/password-reset`),
    onSuccess: async (result) => {
      setError(null);
      setNotice(resetNotice(result, `Password reset sent for ${result.user.email}.`));
      await refresh();
    },
    onError: (caught: unknown) => fail(caught, 'Could not send the password reset.'),
  });

  function useUserAction(path: string, fallback: string, done?: (user: ManagedUser) => string) {
    return useMutation({
      mutationFn: (id: string) => api.post<{ user: ManagedUser }>(`/api/users/${id}/${path}`),
      onSuccess: async (result) => {
        setError(null);
        setNotice(done ? { text: done(result.user) } : null);
        await refresh();
      },
      onError: (caught: unknown) => fail(caught, fallback),
    });
  }

  const deactivate = useUserAction(
    'deactivate',
    'Could not deactivate the account.',
    (u) =>
      `${u.displayName} was deactivated. Their sessions have ended; Show deactivated brings the account back.`,
  );
  const reactivate = useUserAction(
    'reactivate',
    'Could not reactivate the account.',
    (u) => `${u.displayName} is back on the list, with the password and authenticator they had.`,
  );
  const clear2fa = useUserAction(
    'clear-2fa',
    'Could not clear the authenticator.',
    (u) =>
      `Authenticator cleared for ${u.email}. They will enroll a new one at their next sign-in.`,
  );
  const unlock = useUserAction('unlock', 'Could not unlock the account.');

  const busy =
    create.isPending ||
    resendInvite.isPending ||
    sendReset.isPending ||
    deactivate.isPending ||
    reactivate.isPending ||
    clear2fa.isPending ||
    unlock.isPending;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Users"
        description="Accounts, roles, and access recovery. A new account is invited by email; when mail is not set up, the link is shown here to pass on in person."
      />

      {error && <Alert>{error}</Alert>}
      {notice && (
        <Alert tone="success">
          {notice.text}
          {notice.secret && (
            <>
              <p className="mt-2 text-xs text-muted">One-time password:</p>
              <Input
                readOnly
                value={notice.secret}
                onFocus={(e) => e.currentTarget.select()}
                aria-label="One-time password"
                className="mt-1 font-mono text-xs"
              />
            </>
          )}
          {notice.link && (
            <>
              <p className="mt-2 text-xs text-muted">Link (shown once):</p>
              <Input
                readOnly
                value={notice.link}
                onFocus={(e) => e.currentTarget.select()}
                aria-label="Account link"
                className="mt-1 font-mono text-xs"
              />
            </>
          )}
        </Alert>
      )}

      <NewUserForm busy={create.isPending} onSubmit={(input) => create.mutate(input)} />

      {users.isPending && <Spinner label="Loading users" />}
      {users.isError && <Alert>{users.error.message}</Alert>}

      {users.data && (
        <Card>
          <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-2">
            <h2 className="text-sm font-medium text-body">
              {showInactive ? 'Deactivated accounts' : 'Accounts'}
            </h2>
            <label className="ml-auto flex items-center gap-1.5 text-xs text-muted">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-line-strong"
              />
              Show deactivated ({inactive.length})
            </label>
          </div>
          <TableScroll>
            <table className="w-full text-sm">
              <thead className="border-b border-line text-left text-xs text-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Role</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium">Authenticator</th>
                  <th className="px-4 py-2 font-medium">Last sign-in</th>
                  <th className="px-4 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {listed.map((user) => (
                  <UserRow
                    key={user.id}
                    user={user}
                    isSelf={user.id === selfId}
                    busy={busy}
                    onDeactivate={() => {
                      if (
                        window.confirm(
                          `Deactivate ${user.displayName} (${user.email})? Their sessions end at once and they can no longer sign in.`,
                        )
                      )
                        deactivate.mutate(user.id);
                    }}
                    onReactivate={() => reactivate.mutate(user.id)}
                    onClear2fa={() => {
                      if (
                        window.confirm(
                          `Clear the authenticator for ${user.email}? Their sessions end and they enroll a new one at next sign-in.`,
                        )
                      )
                        clear2fa.mutate(user.id);
                    }}
                    onUnlock={() => unlock.mutate(user.id)}
                    onResendInvite={() => resendInvite.mutate(user.id)}
                    onSendReset={() => {
                      if (
                        window.confirm(
                          `Send a password reset for ${user.email}? Their current sessions and remembered devices end when they use the link.`,
                        )
                      )
                        sendReset.mutate(user.id);
                    }}
                  />
                ))}
                {listed.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-sm text-muted">
                      {showInactive ? 'Nobody has been deactivated.' : 'No accounts yet.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </TableScroll>
        </Card>
      )}

      <p className="text-xs text-muted">
        If every administrator is locked out, the server command{' '}
        <code className="rounded bg-sunken px-1">
          npm run db:reset-user -- --email person@example.com --password
        </code>{' '}
        still works from the application directory on the VM.
      </p>
    </div>
  );
}

function NewUserForm({
  busy,
  onSubmit,
}: {
  busy: boolean;
  onSubmit: (input: { email: string; displayName: string; role: Role }) => void;
}) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<Role>('viewer');

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    onSubmit({ email, displayName, role });
    setEmail('');
    setDisplayName('');
    setRole('viewer');
  }

  return (
    <Card className="p-5">
      <h2 className="text-sm font-medium text-body">Add a user</h2>
      <form onSubmit={handleSubmit} className="mt-3 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[2fr_2fr_1fr]">
          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={busy}
            />
          </Field>
          <Field label="Name">
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              disabled={busy}
            />
          </Field>
          <Field label="Role" hint={ROLE_DESCRIPTIONS[role]}>
            <Select value={role} onChange={(e) => setRole(e.target.value as Role)} disabled={busy}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="flex justify-end">
          <Button type="submit" disabled={busy}>
            {busy ? 'Creating…' : 'Create account'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/** The one-time password and link (when present) to show for an account action. */
function inviteNotice(result: InviteResponse, lead: string): Notice {
  if (result.temporaryPassword || result.inviteUrl) {
    return {
      text: `${lead} Mail is not configured, so pass this on in person; it is shown once.`,
      ...(result.temporaryPassword ? { secret: result.temporaryPassword } : {}),
      ...(result.inviteUrl ? { link: result.inviteUrl } : {}),
    };
  }
  return { text: `${lead} An invitation has been emailed.` };
}

function resetNotice(result: InviteResponse, lead: string): Notice {
  if (result.inviteUrl) {
    return {
      text: `${lead} Mail is not configured, so pass this link on in person; it is shown once.`,
      link: result.inviteUrl,
    };
  }
  return { text: `${lead} A reset link has been emailed.` };
}

/** Whether an account has neither enrolled nor signed in — still owes its setup. */
function notSetUp(user: ManagedUser): boolean {
  return !user.totpEnabled && !user.lastLoginAt;
}

/** ", invitation expires in 23 h" or ", invitation expired"; empty when none. */
function inviteSuffix(invite: ManagedUser['invite']): string {
  if (!invite) return '';
  if (invite.expired) return ', invitation expired';
  const hours = Math.max(
    1,
    Math.round((new Date(invite.expiresAt).getTime() - Date.now()) / 3_600_000),
  );
  return `, invitation expires in ${hours} h`;
}

function statusOf(user: ManagedUser): { label: string; tone: string } {
  if (!user.isActive) return { label: 'Deactivated', tone: 'text-danger-text' };
  if (user.lockedUntil && new Date(user.lockedUntil).getTime() > Date.now()) {
    return { label: `Locked until ${dateTime(user.lockedUntil)}`, tone: 'text-warn-text' };
  }
  // A mail-off account carries both a temporary password and a live invitation;
  // show the invitation state beside the password rather than hiding it.
  if (user.mustChangePassword) {
    return {
      label: `Temporary password${inviteSuffix(user.invite)}`,
      tone: user.invite?.expired ? 'text-warn-text' : 'text-muted',
    };
  }
  if (notSetUp(user)) {
    if (user.invite?.expired) return { label: 'Invitation expired', tone: 'text-warn-text' };
    if (user.invite) return { label: 'Invited', tone: 'text-muted' };
    return { label: 'Not set up', tone: 'text-muted' };
  }
  return { label: 'Active', tone: 'text-body' };
}

function UserRow({
  user,
  isSelf,
  busy,
  onDeactivate,
  onReactivate,
  onClear2fa,
  onUnlock,
  onResendInvite,
  onSendReset,
}: {
  user: ManagedUser;
  isSelf: boolean;
  busy: boolean;
  onDeactivate: () => void;
  onReactivate: () => void;
  onClear2fa: () => void;
  onUnlock: () => void;
  onResendInvite: () => void;
  onSendReset: () => void;
}) {
  const status = statusOf(user);
  const locked = user.lockedUntil !== null && new Date(user.lockedUntil).getTime() > Date.now();

  return (
    <tr>
      <td className="px-4 py-2">
        <div className="font-medium text-body">
          {user.displayName}
          {isSelf && <span className="ml-1 text-xs text-faint">(you)</span>}
        </div>
        <div className="text-xs text-muted">{user.email}</div>
      </td>
      <td className="px-4 py-2 text-body">{ROLE_LABELS[user.role]}</td>
      <td className={`px-4 py-2 ${status.tone}`}>{status.label}</td>
      <td className="px-4 py-2 text-muted">{user.totpEnabled ? 'Enrolled' : 'Not enrolled'}</td>
      <td className="px-4 py-2 text-muted">
        {user.lastLoginAt ? dateTime(user.lastLoginAt) : 'Never'}
      </td>
      <td className="px-4 py-2 text-right">
        <div className="flex flex-wrap items-center justify-end gap-1">
          {/* Not yet set up: re-send the invitation, retiring the old link.
              Already set up: a password reset is the way to hand access back. */}
          {user.isActive &&
            (notSetUp(user) ? (
              <Button variant="ghost" onClick={onResendInvite} disabled={busy}>
                Resend invitation
              </Button>
            ) : (
              <Button variant="ghost" onClick={onSendReset} disabled={busy}>
                Send password reset
              </Button>
            ))}
          {user.totpEnabled && !isSelf && (
            <Button variant="ghost" onClick={onClear2fa} disabled={busy}>
              Clear authenticator
            </Button>
          )}
          {locked && (
            <Button variant="ghost" onClick={onUnlock} disabled={busy}>
              Unlock
            </Button>
          )}
          {/* Not yourself: an administrator who deactivated their own account
              would have nothing left to sign in with. */}
          {!isSelf &&
            (user.isActive ? (
              <Button variant="ghost" onClick={onDeactivate} disabled={busy}>
                Deactivate
              </Button>
            ) : (
              <Button variant="ghost" onClick={onReactivate} disabled={busy}>
                Reactivate
              </Button>
            ))}
        </div>
      </td>
    </tr>
  );
}
